import { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  Loader2,
  RefreshCw,
  AlertCircle,
  User,
  Users2,
  Newspaper,
  GraduationCap,
  Filter,
} from 'lucide-react';
import { ReportCard, ReportDetailPanel } from '../../components/Moderation';

// Types
interface Report {
  id: number;
  type: 'USER' | 'SQUAD' | 'ARTICLE' | 'PROVIDER';
  status: string;
  reporterId: number;
  reporterName: string;
  reporterEmail: string;
  targetId: number;
  targetName: string;
  targetType: string;
  reasonCode: string;
  reasonText?: string;
  description?: string;
  contextType?: string;
  contextThreadId?: number;
  contextMessageCursor?: string;
  createdAt: string;
  reviewedAt?: string;
  reviewedById?: number;
  reviewedByName?: string;
  reviewNotes?: string;
}

interface ModerationStats {
  totalPending: number;
  totalResolved: number;
  totalDismissed: number;
  userReportsPending: number;
  squadReportsPending: number;
  articleReportsPending: number;
  providerReportsPending: number;
}

interface PageResponse {
  content: Report[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// API helper
async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `HTTP ${response.status}`);
  }

  return response.json();
}

type ReportType = 'ALL' | 'USER' | 'SQUAD' | 'ARTICLE' | 'PROVIDER';
type StatusFilter = 'PENDING' | 'RESOLVED' | 'DISMISSED' | 'ALL';

export function ModerationQueuePage() {
  // State
  const [reports, setReports] = useState<Report[]>([]);
  const [stats, setStats] = useState<ModerationStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [typeFilter, setTypeFilter] = useState<ReportType>('ALL');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('PENDING');

  // Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Selected report
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isActioning, setIsActioning] = useState(false);

  // Load reports
  const loadReports = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const typeParam = typeFilter !== 'ALL' ? `&type=${typeFilter}` : '';
      const url = `/api/v1/admin/moderation/queue?status=${statusFilter}${typeParam}&page=${page}&size=20`;

      const data = await fetchApi<PageResponse>(url);
      setReports(data.content);
      setTotalPages(data.totalPages);
      setTotalItems(data.totalElements);
    } catch (err) {
      console.error('Failed to load reports:', err);
      // Use mock data for development
      setReports(getMockReports());
      setTotalPages(1);
      setTotalItems(5);
    } finally {
      setIsLoading(false);
    }
  }, [typeFilter, statusFilter, page]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const data = await fetchApi<ModerationStats>('/api/v1/admin/moderation/stats');
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
      // Use mock data for development
      setStats({
        totalPending: 12,
        totalResolved: 45,
        totalDismissed: 8,
        userReportsPending: 5,
        squadReportsPending: 3,
        articleReportsPending: 2,
        providerReportsPending: 2,
      });
    }
  }, []);

  // Load data on mount and filter change
  useEffect(() => {
    loadReports();
    loadStats();
  }, [loadReports, loadStats]);

  // Handle action
  const handleAction = async (action: string) => {
    if (!selectedReport) return;

    setIsActioning(true);
    try {
      await fetchApi(`/api/v1/admin/moderation/queue/${selectedReport.type}/${selectedReport.id}/resolve`, {
        method: 'PUT',
        body: JSON.stringify({ action }),
      });

      // Reload data
      await loadReports();
      await loadStats();
      setSelectedReport(null);
    } catch (err) {
      console.error('Failed to resolve report:', err);
      setError(err instanceof Error ? err.message : 'Failed to resolve report');
    } finally {
      setIsActioning(false);
    }
  };

  // Mock data for development
  function getMockReports(): Report[] {
    return [
      {
        id: 1,
        type: 'USER',
        status: 'PENDING',
        reporterId: 100,
        reporterName: 'John Smith',
        reporterEmail: 'john@example.com',
        targetId: 200,
        targetName: 'Bad Actor',
        targetType: 'User',
        reasonCode: 'HARASSMENT',
        reasonText: 'This user has been sending inappropriate messages in the squad chat.',
        contextType: 'MESSAGE',
        contextThreadId: 5,
        createdAt: new Date(Date.now() - 3600000).toISOString(),
      },
      {
        id: 2,
        type: 'SQUAD',
        status: 'PENDING',
        reporterId: 101,
        reporterName: 'Jane Doe',
        reporterEmail: 'jane@example.com',
        targetId: 50,
        targetName: 'Inappropriate Squad Name',
        targetType: 'Squad',
        reasonCode: 'INAPPROPRIATE_CONTENT',
        reasonText: 'The squad name contains offensive language.',
        createdAt: new Date(Date.now() - 7200000).toISOString(),
      },
      {
        id: 3,
        type: 'ARTICLE',
        status: 'PENDING',
        reporterId: 102,
        reporterName: 'Mike Johnson',
        reporterEmail: 'mike@example.com',
        targetId: 25,
        targetName: 'Article #25',
        targetType: 'Article',
        reasonCode: 'SPAM',
        reasonText: 'This article contains promotional spam content.',
        createdAt: new Date(Date.now() - 86400000).toISOString(),
      },
      {
        id: 4,
        type: 'PROVIDER',
        status: 'PENDING',
        reporterId: 103,
        reporterName: 'Sarah Wilson',
        reporterEmail: 'sarah@example.com',
        targetId: 15,
        targetName: 'Provider #15',
        targetType: 'Provider',
        reasonCode: 'FAKE_CREDENTIALS',
        reasonText: 'This coach claims certifications they do not have.',
        createdAt: new Date(Date.now() - 172800000).toISOString(),
      },
      {
        id: 5,
        type: 'USER',
        status: 'RESOLVED',
        reporterId: 104,
        reporterName: 'Alex Brown',
        reporterEmail: 'alex@example.com',
        targetId: 201,
        targetName: 'Resolved User',
        targetType: 'User',
        reasonCode: 'IMPERSONATION',
        reasonText: 'This user is impersonating another player.',
        createdAt: new Date(Date.now() - 259200000).toISOString(),
        reviewedAt: new Date(Date.now() - 86400000).toISOString(),
        reviewedByName: 'Admin User',
        reviewNotes: 'Action: WARN\nWarning issued to user.',
      },
    ];
  }

  // Type filter buttons config
  const typeFilters = [
    { key: 'ALL', label: 'All', icon: Filter, count: stats?.totalPending },
    { key: 'USER', label: 'Users', icon: User, count: stats?.userReportsPending },
    { key: 'SQUAD', label: 'Squads', icon: Users2, count: stats?.squadReportsPending },
    { key: 'ARTICLE', label: 'Articles', icon: Newspaper, count: stats?.articleReportsPending },
    { key: 'PROVIDER', label: 'Providers', icon: GraduationCap, count: stats?.providerReportsPending },
  ] as const;

  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Shield className="h-7 w-7 text-cyan-400" />
            Moderation Queue
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Review and resolve user reports
          </p>
        </div>

        <button
          onClick={() => { loadReports(); loadStats(); }}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 rounded-lg transition-colors"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <div className="text-2xl font-bold text-amber-400">{stats.totalPending}</div>
            <div className="text-sm text-slate-400">Pending</div>
          </div>
          <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
            <div className="text-2xl font-bold text-green-400">{stats.totalResolved}</div>
            <div className="text-sm text-slate-400">Resolved</div>
          </div>
          <div className="p-4 bg-slate-500/10 border border-slate-500/20 rounded-lg">
            <div className="text-2xl font-bold text-slate-400">{stats.totalDismissed}</div>
            <div className="text-sm text-slate-400">Dismissed</div>
          </div>
          <div className="p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
            <div className="text-2xl font-bold text-cyan-400">
              {stats.totalPending + stats.totalResolved + stats.totalDismissed}
            </div>
            <div className="text-sm text-slate-400">Total</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        {/* Type filter */}
        <div className="flex items-center gap-1 p-1 bg-slate-800 rounded-lg">
          {typeFilters.map(({ key, label, icon: Icon, count }) => (
            <button
              key={key}
              onClick={() => { setTypeFilter(key); setPage(0); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                typeFilter === key
                  ? 'bg-cyan-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
              {count !== undefined && count > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${
                  typeFilter === key ? 'bg-cyan-500' : 'bg-slate-700'
                }`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-1 p-1 bg-slate-800 rounded-lg">
          {(['PENDING', 'RESOLVED', 'DISMISSED', 'ALL'] as const).map((status) => (
            <button
              key={status}
              onClick={() => { setStatusFilter(status); setPage(0); }}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                statusFilter === status
                  ? 'bg-cyan-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
            >
              {status.charAt(0) + status.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Reports list */}
        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="flex items-center gap-3 p-4 mb-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400">
              <AlertCircle className="h-5 w-5" />
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
            </div>
          ) : reports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Shield className="h-12 w-12 mb-3 opacity-50" />
              <p>No reports found</p>
              <p className="text-sm mt-1">Try changing the filters</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {reports.map((report) => (
                <ReportCard
                  key={`${report.type}-${report.id}`}
                  report={report}
                  onClick={() => setSelectedReport(report)}
                  isSelected={selectedReport?.id === report.id && selectedReport?.type === report.type}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
              <div className="text-sm text-slate-500">
                Showing {reports.length} of {totalItems} reports
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 text-sm rounded-lg transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-slate-400">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 text-sm rounded-lg transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selectedReport && (
          <div className="w-96 flex-shrink-0">
            <ReportDetailPanel
              report={selectedReport}
              onClose={() => setSelectedReport(null)}
              onAction={handleAction}
              isActioning={isActioning}
            />
          </div>
        )}
      </div>
    </div>
  );
}
